import * as ResizeObserver from "resize-observer-polyfill"
import { BepoFloatingPlayerTitle } from "./bepoFloatingPlayerTitle"
import { BepoFloatingPlayerFooter, BepoFloatingPlayerFooterVariant } from "./bepoFloatingPlayerFooter"
import { SharedAudioPlayerControls } from "../../../commonComponents/inline/sharedAudioPlayerControls"
import { SiteVideoInline } from "../../../siteWidgets/videoPlayer/genericInlineVideoPlayer/siteVideoInline"
import { SiteVideoData } from "../../../interfaces"
import { SpeedMenuButton } from "../../verticalVideoOverlayPlayer/components/SpeedMenuButton"
require("./ynet_magazine2025_yplus_site_bepoFloatingPlayer.less")

const MOBILE_PLAYER_DISMISS_RATIO = 0.6
const MOBILE_PLAYER_DISMISS_VELOCITY = 0.65
const MOBILE_PLAYER_DISMISS_MIN_OFFSET = 18
const MOBILE_PLAYER_COLLAPSE_ANIMATION_MS = 260

function isFlowplayerAdActive(root: Element): boolean {
    if (!root || !root.classList) {
        return false
    }
    return root.classList.contains("is-ad-linear")
        || root.classList.contains("ad-linear")
        || root.classList.contains("ad-playing")
}

export interface BepoFloatingPlayerProps {
    title: string
    podcastName: string
    podcastImageUrl: string
    podcastPageUrl: string
    bepoHomeUrl: string
    hasMedia: boolean
    videoData?: SiteVideoData
    hideVideoAds?: boolean
    isVideoMode?: boolean
    mediaImageUrl?: string
    isVisible: boolean
    isPlaying: boolean
    isLoading?: boolean
    currentTime: number
    duration: number
    playbackSpeed: number
    isMobileWeb?: boolean
    playerApiRef?: any
    footerVariant?: BepoFloatingPlayerFooterVariant
    onClose: () => void
    onPlayPause: () => void
    onSeek: (time: number) => void
    onForward: () => void
    onBackward: () => void
    onVideoElementChange?: (videoElement: HTMLVideoElement) => void
    onAdPlayingChange?: (isShowingAd: boolean) => void
}

interface BepoFloatingPlayerState {
    bottomOffset: number
    isCollapsed: boolean
    isDraggingSheet: boolean
    isCollapsing: boolean
    dragOffsetY: number
    isShowingAd: boolean
}

function formatStickyPlayerTime(seconds: number): string {
    const safe = Math.max(0, Math.floor(seconds || 0))
    const mins = Math.floor(safe / 60)
    const secs = safe % 60
    const mm = mins < 10 ? `0${mins}` : `${mins}`
    const ss = secs < 10 ? `0${secs}` : `${secs}`
    return `${mm}:${ss}`
}

export class BepoFloatingPlayer extends React.Component<BepoFloatingPlayerProps, BepoFloatingPlayerState> {
    static siteScriptName = "BepoFloatingPlayer"

    constructor(props: BepoFloatingPlayerProps) {
        super(props)
        this.state = {
            bottomOffset: 0,
            isCollapsed: !!props.isMobileWeb,
            isDraggingSheet: false,
            isCollapsing: false,
            dragOffsetY: 0,
            isShowingAd: false,
        }
    }

    private resizeObserver: ResizeObserver
    private adClassObserver: MutationObserver = null
    private videoWrapperRef: HTMLDivElement = null
    private mediaContainerRef: HTMLDivElement = null
    private sheetRef: HTMLDivElement = null
    private flowplayerRoot: Element = null
    private flowplayerApi: any = null
    private preRollAdPlayed = false
    private playerBootstrapDone = false
    private inlineVideoData: SiteVideoData = null
    private videoElementPollInterval: number = 0
    private collapseTimeoutId: number = 0
    private dragStartY: number = 0
    private dragDistance: number = 0
    private dragPointerId: number = null
    private dragSheetHeight: number = 0
    private dragLastY: number = 0
    private dragLastMoveTime: number = 0
    private dragVelocityY: number = 0

    componentDidMount() {
        const { isMobileWeb } = this.props
        const elementId = isMobileWeb ? "ads.banner" : "ads.sargel"
        const stickyAdElement = document.getElementById(elementId)

        this.resizeObserver = new ResizeObserver((records) => {
            records.forEach((r) => {
                this.setState({ bottomOffset: r.contentRect.height })
            })
        })

        stickyAdElement && this.resizeObserver.observe(stickyAdElement)

        if (this.props.isVideoMode) {
            this.startVideoElementPolling()
        }
    }

    componentDidUpdate(prevProps: BepoFloatingPlayerProps) {
        const { isMobileWeb, isVisible } = this.props
        const prevUrl = prevProps.videoData && prevProps.videoData.url
        const nextUrl = this.props.videoData && this.props.videoData.url
        const videoSourceChanged = this.props.isVideoMode && (
            !prevProps.isVideoMode || prevUrl !== nextUrl
        )

        if (videoSourceChanged) {
            this.resetVideoSession()
            this.startVideoElementPolling()
        }

        if (!this.props.isVideoMode && prevProps.isVideoMode) {
            this.stopVideoElementPolling()
            this.resetVideoSession()
            this.props.onVideoElementChange && this.props.onVideoElementChange(null)
        }

        if (isMobileWeb && isVisible && !prevProps.isVisible) {
            this.finishDragTracking()
            this.setState({
                isCollapsed: true,
                isDraggingSheet: false,
                isCollapsing: false,
                dragOffsetY: 0,
            })
        }
    }

    componentWillUnmount() {
        this.resizeObserver && this.resizeObserver.disconnect()
        this.stopVideoElementPolling()
        this.teardownAdTracking()
        this.props.onVideoElementChange && this.props.onVideoElementChange(null)
        if (this.collapseTimeoutId) {
            window.clearTimeout(this.collapseTimeoutId)
            this.collapseTimeoutId = 0
        }
    }

    private resetVideoSession = () => {
        this.teardownAdTracking()
        this.preRollAdPlayed = false
        this.playerBootstrapDone = false
        this.inlineVideoData = null
        this.setShowingAd(false)
    }

    private stopVideoElementPolling = () => {
        if (this.videoElementPollInterval) {
            window.clearInterval(this.videoElementPollInterval)
            this.videoElementPollInterval = 0
        }
    }

    private setShowingAd = (isShowingAd: boolean) => {
        if (this.state.isShowingAd === isShowingAd) {
            return
        }
        this.setState({ isShowingAd })
        this.props.onAdPlayingChange && this.props.onAdPlayingChange(isShowingAd)
    }

    private syncAdStateFromFlowplayerRoot = () => {
        if (!this.flowplayerRoot) {
            return
        }
        if (this.preRollAdPlayed) {
            this.setShowingAd(false)
            return
        }
        this.setShowingAd(isFlowplayerAdActive(this.flowplayerRoot))
    }

    private teardownAdTracking = () => {
        if (this.adClassObserver) {
            this.adClassObserver.disconnect()
            this.adClassObserver = null
        }
        this.flowplayerRoot = null
        this.flowplayerApi = null
    }

    private observeFlowplayerAdClasses = (root: Element) => {
        if (!root || typeof MutationObserver === "undefined") {
            return
        }

        if (this.adClassObserver) {
            this.adClassObserver.disconnect()
        }

        this.flowplayerRoot = root
        this.syncAdStateFromFlowplayerRoot()

        this.adClassObserver = new MutationObserver(() => {
            this.syncAdStateFromFlowplayerRoot()
        })
        this.adClassObserver.observe(root, { attributes: true, attributeFilter: ["class"] })
    }

    private safePlayerPlay = (player: any) => {
        try {
            player.play()
        } catch (e) {
        }
    }

    private handlePlayerLoaded = (player: any) => {
        if (!player) {
            return
        }

        this.flowplayerApi = player
        const root = (player.root || player.el || (this.videoWrapperRef && this.videoWrapperRef.querySelector(".flowplayer"))) as Element
        if (root) {
            this.observeFlowplayerAdClasses(root)
        }

        if (this.playerBootstrapDone) {
            return
        }
        this.playerBootstrapDone = true

        try {
            player.setOpts({ fullscreen: false })
        } catch (e) {
        }

        const shouldPlayPreroll = !this.props.hideVideoAds
        if (shouldPlayPreroll && player.ads && typeof player.ads.on === "function") {
            const onAdDone = () => this.onPrerollFinished(player)
            player.ads.on("ad-completed", onAdDone)
            player.ads.on("ad-skipped", onAdDone)
            player.ads.on("ad-error", onAdDone)
        }

        this.safePlayerPlay(player)
        this.startVideoElementPolling()
    }

    private getInlineVideoData = (): SiteVideoData => {
        const { videoData } = this.props
        if (!videoData) {
            return undefined
        }
        if (!this.inlineVideoData || this.inlineVideoData.url !== videoData.url) {
            this.inlineVideoData = { ...videoData, isAutoPlaying: true }
        }
        return this.inlineVideoData
    }

    private onPrerollFinished = (player: any) => {
        if (this.preRollAdPlayed) {
            return
        }
        this.preRollAdPlayed = true
        this.setShowingAd(false)
        this.safePlayerPlay(player)
    }

    private getFullscreenElement = () => {
        const doc = document as Document & {
            webkitFullscreenElement?: Element
            msFullscreenElement?: Element
        }
        return document.fullscreenElement
            || doc.webkitFullscreenElement
            || doc.msFullscreenElement
            || null
    }

    private isMediaFullscreen = () => {
        return !!(this.mediaContainerRef && this.getFullscreenElement() === this.mediaContainerRef)
    }

    private exitMediaFullscreen = () => {
        const doc = document as Document & {
            webkitExitFullscreen?: () => void
            msExitFullscreen?: () => void
            webkitFullscreenElement?: Element
            msFullscreenElement?: Element
        }

        if (this.flowplayerApi && typeof this.flowplayerApi.isFullscreen === "function"
            && this.flowplayerApi.isFullscreen()
            && typeof this.flowplayerApi.fullscreen === "function") {
            try {
                this.flowplayerApi.fullscreen(false)
            } catch (e) {
            }
        }

        if (typeof document.exitFullscreen === "function" && document.fullscreenElement) {
            document.exitFullscreen().catch(() => {})
            return
        }
        if (typeof doc.webkitExitFullscreen === "function" && doc.webkitFullscreenElement) {
            doc.webkitExitFullscreen()
            return
        }
        if (typeof doc.msExitFullscreen === "function" && doc.msFullscreenElement) {
            doc.msExitFullscreen()
            return
        }

        const video = this.videoWrapperRef && this.videoWrapperRef.querySelector("video") as HTMLVideoElement & {
            webkitDisplayingFullscreen?: boolean
            webkitExitFullscreen?: () => void
        }
        if (video && video.webkitDisplayingFullscreen && typeof video.webkitExitFullscreen === "function") {
            video.webkitExitFullscreen()
        }
    }

    private onMediaDoubleClick = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
    }

    private onExpandMedia = () => {
        if (this.state.isShowingAd) {
            return
        }

        const video = this.videoWrapperRef && this.videoWrapperRef.querySelector("video") as HTMLVideoElement & {
            webkitEnterFullscreen?: () => void
            webkitDisplayingFullscreen?: boolean
            webkitExitFullscreen?: () => void
        }
        const flowplayerIsFullscreen = !!(this.flowplayerApi
            && typeof this.flowplayerApi.isFullscreen === "function"
            && this.flowplayerApi.isFullscreen())

        if (this.isMediaFullscreen() || this.getFullscreenElement() || flowplayerIsFullscreen
            || (video && video.webkitDisplayingFullscreen)) {
            this.exitMediaFullscreen()
            return
        }

        const container = this.mediaContainerRef as HTMLElement & {
            webkitRequestFullscreen?: () => void
            msRequestFullscreen?: () => void
        }

        if (container) {
            if (typeof container.requestFullscreen === "function") {
                container.requestFullscreen().catch(() => {})
                return
            }
            if (typeof container.webkitRequestFullscreen === "function") {
                container.webkitRequestFullscreen()
                return
            }
            if (typeof container.msRequestFullscreen === "function") {
                container.msRequestFullscreen()
                return
            }
        }

        if (video && typeof video.webkitEnterFullscreen === "function") {
            video.webkitEnterFullscreen()
        }
    }

    private startVideoElementPolling = () => {
        const { onVideoElementChange } = this.props
        if (!onVideoElementChange) {
            return
        }

        this.stopVideoElementPolling()

        let attempts = 0
        this.videoElementPollInterval = window.setInterval(() => {
            attempts += 1
            const wrapper = this.videoWrapperRef
            const videoElement = (wrapper && (
                wrapper.querySelector("video.fp-engine")
                || wrapper.querySelector(".flowplayer > video")
                || wrapper.querySelector("video")
            )) as HTMLVideoElement

            if (videoElement) {
                onVideoElementChange(videoElement)

                if (!this.flowplayerRoot && wrapper) {
                    const root = wrapper.querySelector(".flowplayer")
                    if (root) {
                        this.observeFlowplayerAdClasses(root)
                    }
                }

                this.stopVideoElementPolling()
                return
            }

            if (attempts > 40) {
                onVideoElementChange(null)
                this.stopVideoElementPolling()
            }
        }, 150)
    }

    private getSheetHeight = () => {
        const sheetElement = this.sheetRef
        if (!sheetElement || !sheetElement.getBoundingClientRect) {
            return 0
        }
        return sheetElement.getBoundingClientRect().height || 0
    }

    private getDismissOffset = () => {
        const currentSheetHeight = Math.max(this.getSheetHeight(), this.dragSheetHeight, 0)
        const viewportHeight = typeof window !== "undefined" ? window.innerHeight || 0 : 0
        return Math.max(currentSheetHeight + 48, viewportHeight ? viewportHeight * 0.72 : 0)
    }

    private finishDragTracking = () => {
        this.dragPointerId = null
        this.dragStartY = 0
        this.dragDistance = 0
        this.dragSheetHeight = 0
        this.dragLastY = 0
        this.dragLastMoveTime = 0
        this.dragVelocityY = 0
    }

    private collapsePlayerWithAnimation = (targetOffset?: number) => {
        if (!this.props.isMobileWeb || this.state.isCollapsed || this.state.isCollapsing) {
            return
        }

        const dismissOffset = Math.max(typeof targetOffset === "number" ? targetOffset : 0, this.getDismissOffset())
        this.setState({
            isDraggingSheet: false,
            isCollapsing: true,
            dragOffsetY: dismissOffset,
        })
        this.finishDragTracking()

        if (this.collapseTimeoutId) {
            window.clearTimeout(this.collapseTimeoutId)
        }
        this.collapseTimeoutId = window.setTimeout(() => {
            this.collapseTimeoutId = 0
            this.setState({
                isCollapsed: true,
                isCollapsing: false,
                dragOffsetY: 0,
            })
        }, MOBILE_PLAYER_COLLAPSE_ANIMATION_MS)
    }

    private expandPlayer = () => {
        this.finishDragTracking()
        this.setState({
            isCollapsed: false,
            isDraggingSheet: false,
            isCollapsing: false,
            dragOffsetY: 0,
        })
    }

    private handleSheetDragStart = (e) => {
        const { isMobileWeb } = this.props
        const { isCollapsed, isCollapsing } = this.state
        if (!isMobileWeb || isCollapsed || isCollapsing) {
            return
        }

        if (typeof e.button === "number" && e.button !== 0) {
            return
        }

        const pointerId = typeof e.pointerId === "number" ? e.pointerId : null
        const now = Date.now()

        this.dragStartY = typeof e.clientY === "number" ? e.clientY : 0
        this.dragDistance = 0
        this.dragPointerId = pointerId
        this.dragSheetHeight = this.getSheetHeight()
        this.dragLastY = this.dragStartY
        this.dragLastMoveTime = now
        this.dragVelocityY = 0
        this.setState({ isDraggingSheet: true, isCollapsing: false })

        if (e && e.currentTarget && e.currentTarget.setPointerCapture && pointerId !== null) {
            try {
                e.currentTarget.setPointerCapture(pointerId)
            } catch (error) {
            }
        }

        if (e && e.preventDefault) {
            e.preventDefault()
        }
    }

    private handleSheetDragMove = (e) => {
        if (!this.props.isMobileWeb || !this.state.isDraggingSheet) {
            return
        }

        if (this.dragPointerId !== null && typeof e.pointerId === "number" && e.pointerId !== this.dragPointerId) {
            return
        }

        const currentY = typeof e.clientY === "number" ? e.clientY : this.dragStartY
        const delta = currentY - this.dragStartY
        const nextOffset = delta > 0 ? delta : 0
        const now = Date.now()
        const timeDelta = now - this.dragLastMoveTime

        this.dragDistance = nextOffset
        if (timeDelta > 0) {
            this.dragVelocityY = (currentY - this.dragLastY) / timeDelta
        }
        this.dragLastY = currentY
        this.dragLastMoveTime = now
        this.setState({ dragOffsetY: nextOffset })

        if (e && e.preventDefault) {
            e.preventDefault()
        }
    }

    private handleSheetDragEnd = (e) => {
        if (!this.props.isMobileWeb || !this.state.isDraggingSheet) {
            return
        }

        if (this.dragPointerId !== null && typeof e.pointerId === "number" && e.pointerId !== this.dragPointerId) {
            return
        }

        if (e && e.currentTarget && e.currentTarget.releasePointerCapture && this.dragPointerId !== null) {
            try {
                e.currentTarget.releasePointerCapture(this.dragPointerId)
            } catch (error) {
            }
        }

        const shouldCollapseByDrag = this.dragDistance >= this.dragSheetHeight * MOBILE_PLAYER_DISMISS_RATIO
            || (this.dragDistance >= MOBILE_PLAYER_DISMISS_MIN_OFFSET && this.dragVelocityY > MOBILE_PLAYER_DISMISS_VELOCITY)

        this.setState({ isDraggingSheet: false })

        if (shouldCollapseByDrag) {
            this.collapsePlayerWithAnimation(this.dragDistance)
            return
        }

        this.setState({ dragOffsetY: 0 })
        this.finishDragTracking()
    }

    private getSheetStyle = () => {
        const { isMobileWeb, isVisible } = this.props
        const { bottomOffset, isDraggingSheet, isCollapsing, isCollapsed, dragOffsetY } = this.state

        const baseStyle: React.CSSProperties = {
            bottom: isMobileWeb ? bottomOffset : Math.max(75, bottomOffset),
            left: 0,
            right: isMobileWeb ? 0 : "auto",
            zIndex: 10000,
        }

        if (!isMobileWeb) {
            return {
                ...baseStyle,
                transform: isVisible ? "translateY(0)" : "translateY(120%)",
            }
        }

        if (isDraggingSheet || isCollapsing || dragOffsetY > 0) {
            if (isDraggingSheet) {
                return {
                    ...baseStyle,
                    transform: `translateY(${dragOffsetY}px)`,
                    transition: "none",
                }
            }
            if (isCollapsing) {
                return {
                    ...baseStyle,
                    transform: `translateY(${dragOffsetY}px)`,
                    transition: "transform 260ms cubic-bezier(0.22, 0.61, 0.36, 1)",
                }
            }
            return {
                ...baseStyle,
                transform: `translateY(${dragOffsetY}px)`,
            }
        }

        if (isCollapsed) {
            return {
                ...baseStyle,
                zIndex: 9999,
                transform: "translateY(100%)",
                pointerEvents: "none",
            }
        }

        return {
            ...baseStyle,
            transform: isVisible ? "translateY(0)" : "translateY(120%)",
        }
    }

    private pushPlayPauseAnalytics = () => {
        const { title, podcastName, duration, isPlaying } = this.props
        if (podcastName) {
            return
        }
        if (typeof window === "undefined") {
            return
        }
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
            event: isPlaying ? "media_pause" : "media_click_play",
            media_type: "read_aloud",
            media_length: duration,
            media_title: title || "",
            media_player_name: "bepo floating player",
            media_location: "article page",
            media_autoplay: "true",
        })
    }

    private handlePlayPauseClick = () => {
        if (this.state.isShowingAd) {
            return
        }
        this.pushPlayPauseAnalytics()
        this.props.onPlayPause()
    }

    private renderStickyPlayer = () => {
        const { title, isVisible, isPlaying, isLoading, currentTime, duration, onClose } = this.props
        const { bottomOffset, isShowingAd } = this.state
        const progress = duration > 0 ? Math.min(100, Math.max(0, (currentTime / duration) * 100)) : 0
        const timeLabel = formatStickyPlayerTime(currentTime)
        const showLoading = isShowingAd || !!isLoading
        const playPauseClass = [
            "bepo-fp-sticky-play-pause",
            !showLoading && isPlaying ? "playing" : "",
            showLoading ? "loading" : "",
        ].filter(Boolean).join(" ")

        return (
            <div
                className={`bepo-fp-sticky ${isVisible ? "show" : "hide"}`}
                style={{ bottom: bottomOffset }}
                dir="ltr"
            >
                <button
                    type="button"
                    className="bepo-fp-sticky-close"
                    onClick={onClose}
                    aria-label="סגור נגן"
                />
                <div className="bepo-fp-sticky-main" onClick={this.expandPlayer} role="button" tabIndex={0}>
                    <div className="bepo-fp-sticky-titles">
                        <div className="bepo-fp-sticky-title">{title}</div>
                        <div className="bepo-fp-sticky-time">{timeLabel}</div>
                    </div>
                    <button
                        type="button"
                        className={playPauseClass}
                        onClick={(e) => {
                            e.stopPropagation()
                            this.handlePlayPauseClick()
                        }}
                        aria-label={showLoading ? "טוען..." : (isPlaying ? "השהה" : "נגן")}
                        disabled={showLoading}
                    />
                </div>
                <div className="bepo-fp-sticky-progress" aria-hidden="true">
                    <div className="bepo-fp-sticky-progress-fill" style={{ width: `${progress}%` }} />
                </div>
            </div>
        )
    }

    render() {
        const {
            title,
            podcastName,
            podcastImageUrl,
            podcastPageUrl,
            bepoHomeUrl,
            hasMedia,
            videoData,
            isVideoMode,
            mediaImageUrl,
            isVisible,
            isPlaying,
            isLoading,
            currentTime,
            duration,
            playbackSpeed,
            isMobileWeb,
            playerApiRef,
            footerVariant,
            onClose,
            onSeek,
            onForward,
            onBackward,
        } = this.props

        const { isCollapsed, isDraggingSheet, isCollapsing, isShowingAd } = this.state
        const isSponsorFooter = footerVariant === "sponsor"
        const isReadAloudNoPic = isSponsorFooter && !hasMedia

        const sheetClassName = [
            "bepo-fp-wrapper",
            isMobileWeb ? "mobile-web" : "",
            isVisible ? "show" : "hide",
            isDraggingSheet ? "is-dragging" : "",
            isCollapsing ? "is-collapsing" : "",
            isCollapsed ? "is-collapsed" : "",
            isShowingAd ? "is-showing-ad" : "",
        ].filter(Boolean).join(" ")

        return (
            <>
                {isMobileWeb && isCollapsed && this.renderStickyPlayer()}
                <div
                    className={sheetClassName}
                    style={this.getSheetStyle()}
                    dir="rtl"
                    ref={(ref) => { this.sheetRef = ref }}
                    aria-hidden={isMobileWeb && isCollapsed ? true : undefined}
                >
                    {isMobileWeb && (
                        <div
                            className="bepo-fp-gesture-zone"
                            onPointerDown={this.handleSheetDragStart}
                            onPointerMove={this.handleSheetDragMove}
                            onPointerUp={this.handleSheetDragEnd}
                            onPointerCancel={this.handleSheetDragEnd}
                            aria-hidden="true"
                        >
                            <div className="bepo-fp-drag-handle" />
                        </div>
                    )}

                    {!isMobileWeb && (
                        <button className="bepo-fp-close-btn" onClick={onClose} aria-label="סגור נגן" />
                    )}

                    <div className="bepo-fp-content">
                        <div className="bepo-fp-top">
                            {hasMedia && (
                            <div
                                className="bepo-fp-media-container"
                                ref={(ref) => { this.mediaContainerRef = ref }}
                                onDoubleClick={this.onMediaDoubleClick}
                            >
                                    {isVideoMode && videoData && (
                                        <div className="bepo-fp-video-wrapper" ref={(ref) => this.videoWrapperRef = ref}>
                                            <SiteVideoInline
                                                data={this.getInlineVideoData()}
                                                applyStickyness={false}
                                                hideVideoAds={!!this.props.hideVideoAds}
                                                isShareButton={false}
                                                onPlayerLoaded={this.handlePlayerLoaded}
                                            />
                                        </div>
                                    )}
                                    {!isVideoMode && mediaImageUrl && (
                                        <img className="bepo-fp-media-image" src={mediaImageUrl} alt="" />
                                    )}
                                    {isVideoMode && !isShowingAd && (
                                        <button
                                            type="button"
                                            className="bepo-fp-expand-btn"
                                            onClick={this.onExpandMedia}
                                            aria-label="מסך מלא / יציאה ממסך מלא"
                                        />
                                    )}
                                </div>
                            )}

                            <BepoFloatingPlayerTitle
                                title={title}
                                bepoHomeUrl={bepoHomeUrl}
                                multilineClamp={isReadAloudNoPic}
                                hideBepoLogo={isSponsorFooter}
                            />
                        </div>

                        <SharedAudioPlayerControls
                            classNames={{
                                root: "bepo-fp-controllers",
                                controlsRow: "bepo-fp-controls-row",
                                controlsSpacer: "bepo-fp-controls-spacer",
                                middleControls: "bepo-fp-middle-controls",
                                buttonBase: "bepo-fp-btn",
                                forwardButton: "bepo-fp-forward-btn",
                                playPauseButton: "bepo-fp-play-pause-btn",
                                playPausePlayingModifier: "playing",
                                playPauseLoadingModifier: "loading",
                                backwardButton: "bepo-fp-back-btn",
                                progressContainer: "bepo-fp-progress-bar",
                                timeline: "bepo-fp-timeline",
                                timeLabels: "bepo-fp-time-labels",
                                timeLabel: "bepo-fp-time-current",
                                timeLabelCurrent: "bepo-fp-time-current",
                                timeLabelTotal: "bepo-fp-time-total",
                            }}
                            isPlaying={isShowingAd ? false : isPlaying}
                            isLoading={isShowingAd ? true : isLoading}
                            currentTime={currentTime}
                            duration={duration}
                            playbackSpeed={playbackSpeed}
                            onPlayPause={this.handlePlayPauseClick}
                            onSeek={onSeek}
                            onForward={onForward}
                            onBackward={onBackward}
                            speedControl={playerApiRef ? <SpeedMenuButton playerApiRef={playerApiRef} cycleOnTap speedOptions={[1, 1.5, 2, 0.5]} /> : null}
                            showPlayPauseStatus={false}
                            progressBeforeControls={true}
                            gradientActiveColor="var(--bepo-fp-progress-active, #000)"
                            gradientInactiveColor="var(--bepo-fp-progress-inactive, #c5c5c5)"
                            forwardAriaLabel="קדימה 15 שניות"
                            backwardAriaLabel="אחורה 15 שניות"
                            playAriaLabel="נגן"
                            pauseAriaLabel="השהה"
                            loadingAriaLabel="טוען..."
                        />

                        <BepoFloatingPlayerFooter
                            podcastName={podcastName}
                            podcastImageUrl={podcastImageUrl}
                            podcastPageUrl={podcastPageUrl}
                            bepoHomeUrl={bepoHomeUrl}
                            footerVariant={footerVariant}
                        />
                    </div>
                </div>
            </>
        )
    }
}
